import React, { useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useContent } from "../lib/useContent";
import { mediaSrc } from "../lib/media";
import Lightbox from "../components/Lightbox";

export default function Category() {
    const { slug } = useParams();
    const { categories } = useContent();
    const category = categories.find(c => c.slug === slug);
    const [activeSubIdx, setActiveSubIdx] = useState(0);
    const [lightboxIdx, setLightboxIdx] = useState(null);

    const activeSub = useMemo(
        () => (category?.subcategories?.length ? category.subcategories[activeSubIdx] : null),
        [category, activeSubIdx]
    );

    if (!category) return <Navigate to="/portfolio" replace />;

    return (
        <div data-testid="category-page" className="pt-32 md:pt-40">
            {/* Intro */}
            <section className="px-6 md:px-12 lg:px-24">
                <div className="max-w-6xl mx-auto">
                    <Link
                        to="/portfolio"
                        data-testid="back-to-portfolio"
                        className="font-sans-display text-[11px] uppercase tracking-widest-x text-aplum-dim hover:text-aplum-accent transition-colors"
                    >
                        ← Portfolio
                    </Link>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mt-8">
                        <div className="md:col-span-3">
                            <p className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-accent">
                                Series
                            </p>
                        </div>
                        <div className="md:col-span-9">
                            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] tracking-tighter text-aplum">
                                {category.title.split(" ").map((w, i) =>
                                    i === category.title.split(" ").length - 1 ? (
                                        <em key={i} className="italic">{w}</em>
                                    ) : (
                                        <span key={i}>{w} </span>
                                    )
                                )}
                            </h1>
                            <p className="font-sans-display text-base md:text-lg text-aplum-dim mt-8 max-w-2xl leading-relaxed">
                                {category.intro}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {(category.subcategories?.length ?? 0) === 0 ? (
                <section className="px-6 md:px-12 lg:px-24 mt-32 mb-32">
                    <div className="max-w-3xl mx-auto text-center py-32 border border-aplum">
                        <p className="font-serif-display text-3xl text-aplum">In preparation.</p>
                        <p className="font-sans-display text-sm uppercase tracking-widest-x text-aplum-dim mt-4">
                            Works will appear here soon.
                        </p>
                    </div>
                </section>
            ) : (
                <>
                    {/* Subcategory selector */}
                    {(category.subcategories?.length ?? 0) > 1 && (
                        <section className="px-6 md:px-12 lg:px-24 mt-24">
                            <div className="max-w-7xl mx-auto">
                                <p className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-dim mb-6">
                                    Sub-series
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {category.subcategories.map((sub, i) => (
                                        <button
                                            key={sub.slug}
                                            data-testid={`subcategory-${sub.slug}`}
                                            onClick={() => setActiveSubIdx(i)}
                                            className={`category-card text-left group ${
                                                activeSubIdx === i ? "" : "opacity-70 hover:opacity-100"
                                            }`}
                                        >
                                            <div className="relative aspect-[4/3] overflow-hidden">
                                                <img
                                                    src={mediaSrc(sub.artworks[0]?.image)}
                                                    alt={sub.artworks[0]?.image?.alt ?? sub.label}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div
                                                    className={`absolute inset-0 transition-colors duration-500 ${
                                                        activeSubIdx === i
                                                            ? "bg-aplum-accent/30"
                                                            : "bg-aplum/40 group-hover:bg-aplum/20"
                                                    }`}
                                                />
                                                <div className="absolute inset-0 flex items-center justify-center px-4">
                                                    <h3 className="font-serif-display text-xl md:text-2xl text-aplum tracking-tight text-center">
                                                        {sub.label}
                                                    </h3>
                                                </div>
                                                {activeSubIdx === i && (
                                                    <div className="absolute bottom-3 left-3 font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-accent">
                                                        Viewing
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Artwork gallery */}
                    <section className="px-6 md:px-12 lg:px-24 mt-24 md:mt-32">
                        <div className="max-w-7xl mx-auto">
                            <div className="flex items-baseline justify-between mb-12 border-b border-aplum pb-6">
                                <h2 className="font-serif-display text-2xl md:text-3xl tracking-tighter text-aplum">
                                    {activeSub.label}
                                </h2>
                                <span className="font-sans-display text-[11px] uppercase tracking-widest-x text-aplum-dim">
                                    {activeSub.artworks.length} Works
                                </span>
                            </div>

                            <div
                                data-testid="artwork-grid"
                                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16"
                            >
                                {activeSub.artworks.map((art, i) => (
                                    <motion.button
                                        key={art.id}
                                        type="button"
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-50px" }}
                                        transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
                                        onClick={() => setLightboxIdx(i)}
                                        data-testid={`artwork-${art.id}`}
                                        className={`artwork-card block text-left group ${
                                            i % 5 === 0 ? "sm:col-span-2 lg:col-span-2" : ""
                                        }`}
                                    >
                                        <div className="overflow-hidden">
                                            <img
                                                src={mediaSrc(art.image)}
                                                alt={art.image?.alt ?? art.title}
                                                className={`w-full object-cover ${
                                                    i % 5 === 0 ? "aspect-[3/2]" : "aspect-[3/4]"
                                                }`}
                                            />
                                        </div>
                                        <div className="mt-5">
                                            <h3 className="font-serif-display text-xl md:text-2xl text-aplum tracking-tight group-hover:text-aplum-accent transition-colors">
                                                {art.title}
                                            </h3>
                                            <p className="font-sans-display text-[11px] uppercase tracking-widest-x text-aplum-dim mt-2">
                                                {art.medium} · {art.year}
                                            </p>
                                            <p className="font-sans-display text-sm text-aplum-dim mt-3 leading-relaxed line-clamp-2">
                                                {art.description}
                                            </p>
                                        </div>
                                    </motion.button>
                                ))}
                            </div>
                        </div>
                    </section>

                    <Lightbox
                        artworks={activeSub.artworks.map(art => ({
                            ...art,
                            image: mediaSrc(art.image),
                        }))}
                        index={lightboxIdx}
                        onClose={() => setLightboxIdx(null)}
                        onIndexChange={setLightboxIdx}
                    />
                </>
            )}
        </div>
    );
}
